import React, { useState, useEffect } from 'react';
import { Tooltip, Row, Col } from 'reactstrap';
import {
    NSInput, NSInputNumberStepper, NSLabel, NSSelect, NSButton,
} from 'bricks';
import { IOptionAnyValue } from 'bricks/types';
import RenderIf from 'components/RenderIf/RenderIf';
import { IWaterfallAnalysis } from 'views/ProFormaTable/types';
import { numeralFormatterCurrency } from 'ns_libs/formatter';
import { format } from 'date-fns';
import { useProFormaSocketContext } from 'views/ProFormaTable/components/ProForma/socketContext/ProFormaSocketProvider';
import { FEE_TYPE_OPTIONS, INCLUDE_DEV_FEE_OPTIONS } from './constants';
import { ITopLevelAnalysis, ITopLevelAnalysisProps } from './types';
import MetricOutputCard from '../../../MetricOutputCard/MetricOutputCard';
import './TopLevelAnalysis.scss';

const TopLevelAnalysis = ({ onChange }: ITopLevelAnalysisProps) => {
    const { waterfallAnalysis, showCents, handleUpdateWaterfallAnalysis } = useProFormaSocketContext();
    const [includeDevFee, setIncludeDevFee] = useState(false);
    const [showTooltip, setShowTooltip] = useState(false);
    const [includeDevFeeOption, setIncludeDevFeeOption] = useState<IOptionAnyValue>(INCLUDE_DEV_FEE_OPTIONS[0]);

    const toggleTooltip = () => setShowTooltip(prev => !prev);

    const getAumFeeTypeOption = (aumFeeType: any) => {
        const foundOption = FEE_TYPE_OPTIONS.find(option => option.value === aumFeeType);
        return foundOption || FEE_TYPE_OPTIONS[0];
    };

    const [waterfallDetails, setWaterfallDetails] = useState<ITopLevelAnalysis>({
        aumFeeStartDate: waterfallAnalysis?.aumFeeStartDate ? waterfallAnalysis.aumFeeStartDate.split('T')[0] : '',
        aumFeePercentage: waterfallAnalysis?.aumFeePercentage ? parseFloat(waterfallAnalysis.aumFeePercentage) * 100 : 0,
        aumFeePerMonth: waterfallAnalysis?.aumFee ? parseFloat(waterfallAnalysis.aumFee) : 0,
        aumFeeType: getAumFeeTypeOption(waterfallAnalysis?.aumFeeType),
        isSingleGp: waterfallAnalysis?.isSingleGp ?? true,
    });

    useEffect(() => {
        if (waterfallAnalysis) {
            setWaterfallDetails(prev => ({
                ...prev,
                aumFeeStartDate: waterfallAnalysis.aumFeeStartDate ? waterfallAnalysis.aumFeeStartDate.split('T')[0] : prev.aumFeeStartDate,
                aumFeePercentage: waterfallAnalysis.aumFeePercentage
                    ? parseFloat(waterfallAnalysis.aumFeePercentage) * 100 : prev.aumFeePercentage,
                aumFeePerMonth: waterfallAnalysis.aumFee ? parseFloat(waterfallAnalysis.aumFee) : prev.aumFeePerMonth,
                aumFeeType: getAumFeeTypeOption(waterfallAnalysis.aumFeeType),
                isSingleGp: waterfallAnalysis.isSingleGp ?? true,
            }));

            if (waterfallAnalysis.includeDeveloperFee !== undefined) {
                const newOption = INCLUDE_DEV_FEE_OPTIONS.find(
                    option => option.value === waterfallAnalysis.includeDeveloperFee,
                ) || INCLUDE_DEV_FEE_OPTIONS[0];
                setIncludeDevFeeOption(newOption);
                setIncludeDevFee(waterfallAnalysis.includeDeveloperFee);
            }
        }
    }, [waterfallAnalysis]);

    const handleWaterfallDetailChange = (key: keyof IWaterfallAnalysis, value: any) => {
        setWaterfallDetails(prev => ({ ...prev, [key]: value }));
        if (key === 'isSingleGp') {
            onChange(key, value);
        }
        handleUpdateWaterfallAnalysis({ [key]: value });
    };

    const selectOption = (option: IOptionAnyValue) => {
        setIncludeDevFeeOption(option);
        setIncludeDevFee(option.value);
        handleUpdateWaterfallAnalysis({ includeDeveloperFee: option.value });
    };

    const labelClassName = 'pl-0 col-4 font-weight-normal text-dark align-self-center';
    const labelClassNameNoWrap = 'pl-0 col-4 my-auto font-weight-normal text-dark text-nowrap';

    const handleAumFeeStartDateChange = (
        newDate: string | null,
        handleUpdateWaterfallAnalysis: (keyValues: { [key: string]: any }) => void,
    ) => {
        if (!newDate) {
            handleUpdateWaterfallAnalysis({ aumFeeStartDate: null });
            return;
        }

        const [year, month, day] = newDate.split('-').map(Number);
        const selectedDate = new Date(year, month - 1, day);
        const formattedDate = format(selectedDate, 'yyyy-MM-dd');

        handleUpdateWaterfallAnalysis({
            aumFeeStartDate: `${formattedDate}T00:00:00`,
        });
    };

    const handleOnKeyDown = (event: React.KeyboardEvent<HTMLInputElement> | null, keyValues: any) => {
        if (!event) handleUpdateWaterfallAnalysis(keyValues);
        else if (event.key === 'Enter') handleUpdateWaterfallAnalysis(keyValues);
        else if (event.key === 'Escape') {
            // Reset to previous values from waterfallAnalysis
            setWaterfallDetails(prev => ({
                ...prev,
                aumFeeStartDate: waterfallAnalysis?.aumFeeStartDate || '',
                aumFeePercentage: waterfallAnalysis?.aumFeePercentage ? parseFloat(waterfallAnalysis.aumFeePercentage) * 100 : 0,
                aumFeePerMonth: waterfallAnalysis?.aumFee ? parseFloat(waterfallAnalysis.aumFee) : 0,
                aumFeeType: getAumFeeTypeOption(waterfallAnalysis?.aumFeeType),
            }));
        }
    };

    return (
        <Row className="mx-n1 px-3">
            <Col className="px-1 mb-2" sm={12} md={6}>
                <div className="w-100">
                    <div className="d-flex align-items-center mt-2">
                        <NSLabel className="pl-0 col-4 my-auto font-weight-normal text-dark text-nowrap">Include dev. fee</NSLabel>
                        <NSSelect
                            options={INCLUDE_DEV_FEE_OPTIONS}
                            name="includeDevFee"
                            value={includeDevFeeOption}
                            onChange={option => selectOption(option)}
                            isClearable={false}
                            formClassName="TopLevelAnalysis__input-width"
                        />
                    </div>

                    <div className="d-flex align-items-center mt-2">
                        <div className="pl-0 col-4 my-auto d-flex flex-column">
                            <NSLabel className="p-0 m-0 font-weight-normal text-dark text-nowrap">AUM fee type</NSLabel>
                            <NSLabel id="aum-tooltip-wrapper" className="TopLevelAnalysis__label-tooltip p-0 text-info font-10">
                                What is AUM?
                            </NSLabel>
                            <Tooltip target="aum-tooltip-wrapper" isOpen={showTooltip} toggle={toggleTooltip} autohide={false}>
                                Assets under management
                            </Tooltip>
                        </div>
                        <NSSelect
                            options={FEE_TYPE_OPTIONS}
                            name="aumFeeType"
                            value={waterfallDetails.aumFeeType}
                            onChange={option => {
                                setWaterfallDetails(prev => ({ ...prev, aumFeeType: option }));
                                handleUpdateWaterfallAnalysis({ aumFeeType: option.value });
                            }}
                            isClearable={false}
                            formClassName="TopLevelAnalysis__input-width"
                        />
                    </div>

                    <div className="d-flex align-items-center mt-2">
                        <NSLabel className={labelClassNameNoWrap}>AUM fee</NSLabel>
                        <NSInputNumberStepper
                            percentage
                            name="aumFeePercentage"
                            value={waterfallDetails.aumFeePercentage}
                            onChange={(valueToUpdate: string) => handleUpdateWaterfallAnalysis({
                                aumFeePercentage: valueToUpdate
                                    ? String(parseFloat(valueToUpdate) / 100) : '0',
                            })}
                            min={0}
                            step={0.1}
                            inputGroupClassName="TopLevelAnalysis__input-width"
                        />
                    </div>

                    {/* Fee Start */}
                    <div className="d-flex align-items-center mt-2">
                        <NSLabel className={labelClassNameNoWrap}>AUM fee start</NSLabel>
                        <NSInput
                            type="date"
                            name="aumFeeStartDate"
                            value={waterfallDetails.aumFeeStartDate}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                setWaterfallDetails(prev => ({ ...prev, aumFeeStartDate: event.target.value }));
                            }}
                            onBlur={() => handleAumFeeStartDateChange(
                                waterfallDetails.aumFeeStartDate,
                                handleUpdateWaterfallAnalysis,
                            )}
                            onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => handleOnKeyDown(event, {
                                aumFeeStartDate: waterfallDetails.aumFeeStartDate,
                            })}
                            inputClassName="TopLevelAnalysis__input-width"
                        />
                    </div>

                    {/* GP */}
                    <div className="d-flex align-items-center mt-2">
                        <NSLabel className={labelClassName}>GP structure</NSLabel>
                        <div className="d-flex TopLevelAnalysis__toggle-buttons">
                            <NSButton
                                data-testid="no-retail-button"
                                className="TopLevelAnalysis__toggle-buttons--left-button"
                                callback={() => handleWaterfallDetailChange('isSingleGp', true)}
                                color={waterfallDetails.isSingleGp ? 'primary' : 'secondary'}
                            >
                                Basic
                            </NSButton>
                            <NSButton
                                data-testid="retail-button"
                                className="TopLevelAnalysis__toggle-buttons--right-button"
                                callback={() => handleWaterfallDetailChange('isSingleGp', false)}
                                color={!waterfallDetails.isSingleGp ? 'primary' : 'secondary'}
                            >
                                Multiple GPs
                            </NSButton>
                        </div>
                    </div>
                </div>
            </Col>
            <Col className="px-1 mb-2" sm={12} md={6}>
                <div>
                    <RenderIf isTrue={includeDevFee}>
                        <div className="mb-2">
                            <MetricOutputCard
                                label="Developer fee"
                                value={numeralFormatterCurrency(waterfallAnalysis.developerFee, showCents ? '$0,0.00' : '$0,0')}
                            />
                        </div>
                    </RenderIf>
                    <MetricOutputCard
                        label="AUM fee"
                        value={`${numeralFormatterCurrency(
                            waterfallDetails.aumFeePerMonth, showCents ? '$0,0.00' : '$0,0',
                        )} per month`}
                    />
                </div>
            </Col>
        </Row>
    );
};

export default TopLevelAnalysis;
